/**
 * BL-1043 (ADR 033) — `canApplyLotTransition`: the shared write-side guard every
 * caller of a lot lifecycle transition consults, keyed on the ADR-023 derived
 * `LotPhase.kind` × the requested {@link LotTransition}. The write-side sibling
 * of `can-batch-apply.ts` (the batch change-axis) and `lot-field-editability.ts`
 * (the field-axis) — all three read the same derived phase, so a UI that offers
 * a transition and the server that performs it can never drift.
 *
 * Pure IsomorphicService (ADR 010 / sharing.md §3): no I/O, no auth, no stamp
 * writes — it only reads phase. Client-safe (nothing server-only transits), safe
 * inside `useMemo` (a future bulk/dropdown UI pre-disables ineligible actions).
 *
 * **Scope.** This guard decides `withdraw` (BL-1043), `open-for-bidding` +
 * `open-for-pre-bidding` (BL-1044), and `reopen` (BL-1169, the first un-terminal
 * transition — decided before the global terminal check). The remaining ADR transition catalog is
 * enumerated in {@link LotTransition} so the shape is greppable, but asking the
 * guard about an unbuilt transition **throws** {@link TransitionNotImplementedError}
 * rather than returning `{ allowed: true }` — a silent allow on an unbuilt path is
 * the failure this guards against. Each lands in its own consolidation card
 * (close/dispose → the financial-fence cards). **This guard did NOT fold into
 * `canBatchApply`** — BL-1071 shipped without it (it moved the auction-phase
 * ceiling into `lot-status-ceiling.ts` and retired the duplicate lock-set
 * barrel), and BL-1073 confirmed the split stands, for the reason spelled out
 * below. Corrected 2026-08-19; the prior wording promised a convergence that
 * never happened. For the open transitions the guard decides the
 * LOT-PHASE axis only — the openability fields + auction-phase gate stay in
 * `LotStatusTransitionService`'s outcome union. Deliberately NOT folded into `canBatchApply` now: its `BatchChange`
 * union is the batch catalog-action surface whose matrix intro certifies it
 * "only reads phase, never stamps" — adding stamp-writing transitions there
 * forces a re-sign of `batch-edit-transition-matrix.md`, which the ADR sequences
 * into the bulk-action cards (BL-1050/1052), not this foundation.
 *
 * **Financially-dangerous transitions stay off this axis by construction** —
 * hammer-adjust, reverse-sale, and swap-buyer are absent from
 * {@link LotTransition}; they remain on their guarded routes / the invoice
 * Price adjustment (`LotPriceAdjustmentService`, `hammer-price-sot.md`).
 */
import type { LotPhaseKind } from './phase-tokens';
import { isLotPhaseTerminal } from './lot-phase-predicates';
import type { BatchApplyVerdict } from './can-batch-apply';

/**
 * The ADR-033 lot transition catalog (Decision 1). Only `withdraw` is decided by
 * this guard today; the rest throw {@link TransitionNotImplementedError} until
 * their consolidation card lands.
 */
export type LotTransition =
  | 'withdraw'
  | 'reopen'
  | 'open-for-bidding'
  | 'open-for-pre-bidding'
  | 'mark-ready'
  | 'mark-draft'
  | 'dispose'
  | 'close'
  | 'soft-delete'
  // BL-1289: the lot mirror of the auction's `restore`. Matrix §3.9/§3.10 has
  // specified it since the 2026-08-18 sign-off (the grid's Deleted row is
  // `restore→` across the board) but the union never carried it, so a deleted
  // lot had no way back. Adding a transition is a `lifecycle-change-consent.md`
  // value-set change — signed off in the BL-1289 build conversation, 2026-08-20.
  | 'restore';

/**
 * An id the transition engine can declare EFFECTS for — a **superset** of
 * {@link LotTransition}, and deliberately a separate type (BL-1507).
 *
 * The two vocabularies answer different questions and had been the same set only
 * by coincidence:
 *
 * - {@link LotTransition} is the **operator ladder**. `LOT_TRANSITION_CATALOG` is
 *   exhaustive over it, and `transitionOptionsFrom` folds that catalog into the
 *   status menu's row set and the Status Reference page. A member here IS a row
 *   an operator sees.
 * - `LotEffectDeclarationId` is **what the preview API can compute a disclosure
 *   for**. Anything with a declared `TransitionDef` qualifies, whether or not it
 *   is its own ladder rung.
 *
 * `dispose-no-sale` is the case that forced the split. It is a real operator
 * action with real declared effects, but its menu row is built by
 * `buildBulkResultRow` / `buildResultRow` from the `dispose-no-sale` BATCH
 * action — so adding it to `LotTransition` put a duplicate No Sale row on the
 * ladder and on the reference page, and left it with no honest `status`
 * (`live` implies a rung it must not have; `planned` requires an empty `from`).
 *
 * Adding a member HERE is not a `lifecycle-change-consent.md` value-set change —
 * the lifecycle vocabulary is unchanged. Adding one to {@link LotTransition}
 * still is.
 */
export type LotEffectDeclarationId = LotTransition | 'dispose-no-sale';

/** The set of transitions this guard decides today (the rest throw). */
const IMPLEMENTED_TRANSITIONS = new Set<LotTransition>([
  'withdraw',
  'reopen',
  'open-for-bidding',
  'open-for-pre-bidding',
  // BL-1296 — the FENCED tier landed: both carry hammer price into the invoice
  // pipeline, and their postCommit effects are now declared (matrix §3.6/§3.7).
  'dispose',
  'close',
  'mark-ready',
  'mark-draft',
  'soft-delete',
  'restore',
]);

/**
 * Thrown when a lifecycle transition method or the guard is asked about a
 * transition whose consolidation card has not landed. Both the pure guard and
 * the app-layer transition-service stubs throw THIS one class so "not built
 * yet" has a single, greppable identity.
 */
export class TransitionNotImplementedError extends Error {
  constructor(transition: string, service?: string) {
    super(
      `${service ? `${service}: ` : ''}transition "${transition}" is not implemented yet — its consolidation card (PRJ-40) has not landed`,
    );
    this.name = 'TransitionNotImplementedError';
  }
}

/**
 * Decide whether a lot in its current derived phase may make the requested
 * lifecycle transition.
 *
 * @param input.lotPhase   The lot's ADR-023 derived phase (from `deriveLotPhase`
 *   or a snapshot). Typed on the minimal `{ kind }` shape (sharing.md §3 rule 2)
 *   — a full `LotPhase` satisfies it structurally.
 * @param input.transition The requested transition. `withdraw`,
 *   `open-for-bidding`, and `open-for-pre-bidding` are decided; any other throws
 *   {@link TransitionNotImplementedError}.
 * @param input.hasBids     Present for the shared signature; the decided rules
 *   ignore it (withdraw cancels active max-bids; the open rules are phase-only).
 * @returns {@link BatchApplyVerdict}.
 * @throws {TransitionNotImplementedError} for an unbuilt transition.
 * @sideEffect none — pure.
 */
export function canApplyLotTransition(input: {
  lotPhase: { kind: LotPhaseKind };
  transition: LotTransition;
  hasBids?: boolean;
}): BatchApplyVerdict {
  const { lotPhase, transition } = input;

  if (!IMPLEMENTED_TRANSITIONS.has(transition)) {
    throw new TransitionNotImplementedError(transition);
  }

  // Reopen (BL-1169) — the first UN-TERMINAL transition (ADR 033 governs
  // transitions in ANY direction), so it MUST be decided BEFORE the global
  // terminal-immutability check below (which every OTHER transition falls into).
  // The inverse of that check:
  //   - terminal-but-not-deleted (sold/unsold/withdrawn) → allowed.
  //   - deleted → not-reopenable (a soft-deleted lot is restored, not reopened).
  //   - every non-terminal phase → not-reopenable (the lot isn't closed).
  // Keys on phase only. The invoice Paid / settlement Approved gates that once
  // sat in ReopenLotService went with BL-1982, and nothing restores them yet.
  if (transition === 'reopen') {
    if (lotPhase.kind === 'deleted') {
      return { allowed: false, reason: 'not-reopenable' };
    }
    return isLotPhaseTerminal(lotPhase)
      ? { allowed: true }
      : { allowed: false, reason: 'not-reopenable' };
  }

  // Restore (BL-1289) — like `reopen`, an UN-terminal transition, so it MUST be
  // decided before the terminal-immutability check below. Matrix §3.10: from
  // `deleted` and nowhere else. The operator picks the landing phase; that
  // choice is the form, not a guard input.
  if (transition === 'restore') {
    return lotPhase.kind === 'deleted'
      ? { allowed: true }
      : { allowed: false, reason: 'not-restorable' };
  }

  // Soft-delete (BL-1289) — matrix §3.9, as AMENDED by BL-1431 (operator
  // sign-off 2026-08-31). Allowed from every phase except `deleted` and `sold`.
  //
  // The non-sold terminals (`withdrawn` / `unsold`) ARE allowed, so
  // this stays decided ahead of the global terminal check below.
  //
  // `sold` was allowed until BL-1431 ("deleting a sold lot is a real operator
  // action"). It is now fenced, for the same reason every OTHER row on a sold
  // lot routes to Reverse Sale (M4): leaving a sale carries financial teardown.
  // Delete was the lone exception, and the only sold lot the old rule actually
  // reached was one sold off-platform (`dispose` from a pre-open phase) — zero
  // bids, invoice already issued — i.e. precisely the lot whose invoice would
  // end up pointing at a row the operator can no longer see.
  //
  // The `SoftDeleteService` restrict/guard blockers (ADR 018) remain a live-data
  // precondition the service owns, not a phase question.
  if (transition === 'soft-delete') {
    if (lotPhase.kind === 'deleted') return { allowed: false, reason: 'already-in-phase' };
    if (lotPhase.kind === 'sold') return { allowed: false, reason: 'not-deletable' };
    return { allowed: true };
  }

  // Dispose (BL-1296) — matrix §3.6. Like `reopen` / `restore` / `soft-delete`,
  // it accepts TERMINAL sources, so it MUST be decided before the global
  // terminal-immutability check below.
  //
  // The 2026-08-18 terminal rule: any terminal phase may move to any OTHER
  // terminal phase as a clerical correction (e.g. `withdrawn`→`sold`), without
  // a reopen round-trip. BL-1387 PR 2 retired the `passed` phase, so the
  // `unsold`→`passed` example the rule was originally written with is gone —
  // a reserve-not-met lot IS `unsold`, and its `disposition` carries the
  // `passed` label. `sold` is the one exception —
  // leaving a sale is the only terminal exit carrying financial teardown, so it
  // keeps its fenced Reverse Sale service and is refused here.
  if (transition === 'dispose') {
    if (lotPhase.kind === 'sold' || lotPhase.kind === 'deleted') {
      return { allowed: false, reason: 'not-disposable' };
    }
    // BL-1428 (M3, signed 2026-08-26): every phase EXCEPT `sold` / `deleted`.
    // The pre-open phases (`draft` / `ready` / `pre-bidding`) used to be refused
    // here on the reasoning that a lot which never opened has no result to
    // record. Operators record sales handled off-platform, so the block is gone
    // — matrix §3.6, and the Draft row of §3.0's non-terminal grid.
    return { allowed: true };
  }

  // Close (BL-1296) — matrix §3.7. The timed close freezes an outcome whose
  // close time has already passed, so ONLY those two phases qualify. Decided
  // before the terminal check so a re-run against an already-closed lot answers
  // `not-closeable` (the honest reason) rather than the generic `terminal`.
  if (transition === 'close') {
    return lotPhase.kind === 'awaiting-resolution' || lotPhase.kind === 'last-call'
      ? { allowed: true }
      : { allowed: false, reason: 'not-closeable' };
  }

  // Terminal lots are immutable for EVERY other transition — a resolved lot can't
  // be withdrawn or (re)opened.
  //
  // BL-1293 AC 4: `sold` answers the more specific `sold-lock` rather than the
  // generic `terminal`. The two phrase differently downstream — `REASON_PHRASE`
  // renders `sold-lock` as "already sold" and `terminal` as the five-phase list
  // "already sold, unsold, withdrawn, or deleted" — so an all-sold bulk
  // selection can name its actual blocker ("3 of 12 selected lots are already
  // sold") instead of reciting every phase it might have been. Naming the real
  // cause is rule #38 §6's "a disabled row names the fix, not the refusal".
  //
  // This is a REASON refinement, not a matrix change: `sold` was already blocked
  // here and still is. Matrix §3.5 keeps `withdraw` blocked from every terminal
  // phase; a terminal lot reaches `withdrawn` via `dispose` (§3.6).
  //
  // Only `sold` is split out. The other terminal phases stay on `terminal`
  // because splitting them too would add four members to `BatchBlockReason`,
  // breaking the exhaustive `Record<BatchBlockReason, string>` tables in
  // `batch-check-presentation.ts` and `lot-status-transition-service.ts` for no
  // graded benefit. A MIXED terminal selection is re-collapsed to `terminal` by
  // `uniformReason` (batch-lot-check-service.ts) so it keeps the honest list.
  if (isLotPhaseTerminal(lotPhase)) {
    return { allowed: false, reason: lotPhase.kind === 'sold' ? 'sold-lock' : 'terminal' };
  }

  if (transition === 'withdraw') {
    // Withdraw (the ADR reference transition). Broadened TWICE:
    //   - 2026-08-11: from the active-only `isLotPhaseActive` gate to "opened for
    //     at least pre-bidding, not yet terminal".
    //   - 2026-08-18 (matrix §3.5 "Changed (a)", landed here in BL-1289): `draft`
    //     and `ready` are now allowed too. The old gate reasoned that a
    //     never-opened lot has nothing to withdraw — but operators want
    //     *Withdrawn* as a RECORD that a consignor pulled an item, which deleting
    //     the lot destroys.
    // Every non-terminal phase is now withdrawable, so there is no phase branch
    // left; `not-withdrawable` survives in the vocabulary for the batch verdicts.
    return { allowed: true };
  }

  // mark-draft (BL-1289) — matrix §3.1: every non-terminal phase except `draft`
  // itself. Terminal was already refused above; `draft` is the no-op cell the
  // grid renders as `—`.
  //
  // BL-1428 / M1 — DELIBERATELY NOT WIDENED, and deliberately still decided
  // AFTER the terminal check. M1 was FILED as "mark-draft allowed from every
  // phase except draft/deleted, decided before the terminal check". Matrix §7i
  // point 1 names that wording wrong; the §3.0 M1 box gives the reason: this
  // transition's `postCommit` is EMPTY, while `reopen` — which already lands
  // `draft` — carried the financial teardown at the time. BL-1982 (PRJ-76
  // TEAR-3) removed that teardown, so the money reason no longer holds, but the
  // routing below is still the signed matrix: widening `mark-draft`'s source set
  // is a lifecycle change that needs its own rule #23 sign-off.
  //
  // M1's real guarantee ("the Draft row is never disabled") lives in ROUTING,
  // resolved by source — `mark-draft` from any non-terminal, `reopen→draft`
  // from `unsold`/`withdrawn`, Reverse Sale from `sold`, `restore→draft` from
  // `deleted`. No transition's source set changed. Do not move this branch.
  if (transition === 'mark-draft') {
    return lotPhase.kind === 'draft'
      ? { allowed: false, reason: 'already-in-phase' }
      : { allowed: true };
  }

  // mark-ready (BL-1289) — matrix §3.2: `draft` ONLY. Invariant I2 (no
  // operator-set `ready` inside a live auction) is an AUCTION-phase precondition
  // the service owns, not a lot-phase question, so it is not a branch here —
  // same split `open-for-bidding` already makes for `auction-not-live`.
  if (transition === 'mark-ready') {
    if (lotPhase.kind === 'ready') return { allowed: false, reason: 'already-in-phase' };
    return lotPhase.kind === 'draft'
      ? { allowed: true }
      : { allowed: false, reason: 'not-openable' };
  }

  if (transition === 'open-for-bidding') {
    // BL-1044 — phase-level eligibility to stamp `bidding_opened_at`. This is the
    // LOT-PHASE axis only: the structural openability fields (ring/lot#/close_time)
    // and the auction-phase gate (auction must be Active + not paused) stay in the
    // service outcome union (`LotStatusTransitionService.openForBiddingTx`) — a
    // `ready`-phase lot the guard allows can still fail the service's MISSING_FIELDS
    // / AUCTION_NOT_ACTIVE checks. The single-lot path keeps its richer error codes;
    // this guard is the coarser axis a future bulk-open consumer (BL-1052) routes
    // through `verdictForBatch`.
    //   - ready | pre-bidding | draft → allowed.
    //   - paused/already-live → not-openable (nothing to open).
    //
    // BL-1428 (M2, matrix §3.4, signed 2026-08-26): `draft` was `not-openable`
    // until now, on the reasoning that a draft lot has nothing to open.
    // Operators wanted the one-step move, and it is safe because the open is a
    // COMPOSITE: `stampLotOpened` writes `bidding_opened_at` AND COALESCEs
    // `readied_at` in ONE UPDATE with one `markMutated`, so the lot passes
    // THROUGH `ready` inside the transaction without ever resting there.
    // Invariant I2 bans an OPERATOR-SET `ready` lot inside a live auction, not a
    // transient one, so it stands unamended — do not "fix" this back.
    if (
      lotPhase.kind === 'ready' ||
      lotPhase.kind === 'pre-bidding' ||
      lotPhase.kind === 'draft'
    ) {
      return { allowed: true };
    }
    return { allowed: false, reason: 'not-openable' };
  }

  // open-for-pre-bidding — the AUCTION-level sweep still opens the `ready` set
  // only (`OpenPreBiddingService` selects `lotPhaseWhere('ready')`). The per-lot
  // operator door is wider: BL-1428 (M2, matrix §3.3) added `draft` as a
  // COMPOSITE source, on the same reasoning as `open-for-bidding` above —
  // `stampLotPreBidding` COALESCEs `readied_at` alongside `pre_bidding_at` in
  // one UPDATE, so `ready` is passed through, never rested in (I2 intact).
  // Anything else is past pre-bidding, paused, or already live.
  if (lotPhase.kind === 'ready' || lotPhase.kind === 'draft') {
    return { allowed: true };
  }
  return { allowed: false, reason: 'not-openable' };
}
